/**
 * CR055/CR071: Merchant Sources Admin Page
 * CR306: Added URL-based merchant filtering (deep-link from Merchant Directory)
 * CR313: UUID-first filtering with merchant_key fallback
 * CR454: Test Image Capture action
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, RefreshCw, Database, AlertTriangle, Activity, X, Filter } from "lucide-react";
import { useMerchantSources, type MerchantSource } from "@/hooks/useMerchantSources";
import { useMerchants } from "@/hooks/useMerchants";
import {
  SourcesTable,
  AddSourceModal,
  EditSourceModal,
  SourceTypeSummary,
} from "@/components/admin/merchant-sources";
import { TestImageCaptureModal } from "@/components/admin/merchant-sources/TestImageCaptureModal";
import { useToast } from "@/hooks/use-toast";
import { Link, useSearchParams } from "react-router-dom";

// CR313: Filter state type for UUID-first filtering
interface MerchantFilterState {
  merchantUuid: string | null;
  merchantKey: string | null;
}

export default function MerchantSources() {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const {
    sources,
    loading,
    fetchSources,
    addSource,
    updateSource,
    deleteSource,
    toggleStatus,
    deprecateSource,
    updatePriority,
    getMerchantsSummary,
  } = useMerchantSources();

  // CR071: Merchants data for logo display/edit
  const {
    merchants,
    loading: merchantsLoading,
    updateMerchantLogo,
    uploadMerchantLogo,
  } = useMerchants();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editSource, setEditSource] = useState<MerchantSource | null>(null);
  const [testCaptureSource, setTestCaptureSource] = useState<MerchantSource | null>(null);
  const [activeTab, setActiveTab] = useState("all");
  
  // CR313: Merchant filter state (UUID-first with key fallback)
  const [merchantFilter, setMerchantFilter] = useState<MerchantFilterState>({
    merchantUuid: null,
    merchantKey: null,
  });
  const didHydrateFromQuery = useRef(false);

  // CR313: Check if any filter is active
  const hasActiveFilter = merchantFilter.merchantUuid || merchantFilter.merchantKey;

  // CR313: Hydrate filter from URL on mount (one-time, UUID-first)
  useEffect(() => {
    if (didHydrateFromQuery.current) return;
    
    const merchantUuid = searchParams.get("merchant_uuid");
    const merchantKey = searchParams.get("merchant_key") || searchParams.get("merchant"); // support legacy param
    
    // Validate UUID format if provided
    const isValidUuid = merchantUuid && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(merchantUuid);
    
    if (isValidUuid || merchantKey) {
      setMerchantFilter({
        merchantUuid: isValidUuid ? merchantUuid : null,
        merchantKey: merchantKey || null,
      });
    }
    didHydrateFromQuery.current = true;
  }, [searchParams]);

  // CR313: Clear filter handler
  const clearMerchantFilter = () => {
    setMerchantFilter({ merchantUuid: null, merchantKey: null });
    setSearchParams((prev) => {
      prev.delete("merchant_uuid");
      prev.delete("merchant_key");
      prev.delete("merchant"); // clear legacy param too
      return prev;
    });
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  const merchantsSummary = getMerchantsSummary();
  const activeSources = sources.filter((s) => s.status === "active");
  const pausedSources = sources.filter((s) => s.status === "paused");
  const deprecatedSources = sources.filter((s) => s.status === "deprecated");
  const merchantsWithNoActive = merchantsSummary.filter((m) => m.active_sources === 0);

  const handleTestRead = (source: MerchantSource) => {
    toast({
      title: "Test Read",
      description: `Test read for ${source.source_url} would be triggered here.`,
    });
    // TODO: Implement actual test read via edge function
  };

  // CR313: Apply merchant filter first (UUID-first, key fallback), then tab filter
  const getFilteredSources = () => {
    let filtered = sources;
    
    // Step 1: Apply UUID filter first if available
    if (merchantFilter.merchantUuid) {
      // Filter by merchant_uuid column (new FK column)
      filtered = sources.filter((s) => (s as any).merchant_uuid === merchantFilter.merchantUuid);
      
      // If UUID filter returns no results but we have a key fallback, try that
      if (filtered.length === 0 && merchantFilter.merchantKey) {
        filtered = sources.filter((s) => s.merchant_id === merchantFilter.merchantKey);
      }
    } else if (merchantFilter.merchantKey) {
      // Legacy fallback: filter by merchant_id (which stores merchant_key)
      filtered = sources.filter((s) => s.merchant_id === merchantFilter.merchantKey);
    }

    // Step 2: Apply tab-based status filter
    switch (activeTab) {
      case "active":
        return filtered.filter((s) => s.status === "active");
      case "paused":
        return filtered.filter((s) => s.status === "paused");
      case "deprecated":
        return filtered.filter((s) => s.status === "deprecated");
      case "no-sources":
        return filtered.filter((s) =>
          merchantsWithNoActive.some((m) => m.merchant_id === s.merchant_id)
        );
      default:
        return filtered;
    }
  };

  // CR313: Get display name for filtered merchant (check by UUID first, then key)
  const filteredMerchantName = useMemo(() => {
    if (!hasActiveFilter) return null;
    
    let merchant = merchantFilter.merchantUuid
      ? merchants.find((m) => m.id === merchantFilter.merchantUuid)
      : null;
    
    if (!merchant && merchantFilter.merchantKey) {
      merchant = merchants.find((m) => m.merchant_key === merchantFilter.merchantKey);
    }
    
    return merchant?.display_name || merchantFilter.merchantKey || "Unknown Merchant";
  }, [merchants, merchantFilter, hasActiveFilter]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Merchant Sources</h1>
            <p className="text-muted-foreground">
              Manage global source URLs for each merchant. Sources are used during READ/SCAN phases.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-medium text-blue-600">PRIMARY (1–9)</span>: Always scanned.{" "}
              <span className="font-medium text-amber-600">FALLBACK (10+)</span>: Used only if PRIMARY returns no candidates.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchSources} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/source-health">
                <Activity className="h-4 w-4 mr-2" />
                Health Dashboard
              </Link>
            </Button>
            <Button onClick={() => setAddModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Source
            </Button>
          </div>
        </div>

        {/* CR313: Active merchant filter badge */}
        {hasActiveFilter && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="flex items-center gap-1.5 py-1 px-3">
              <Filter className="h-3 w-3" />
              <span>Showing sources for: <strong>{filteredMerchantName}</strong></span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 ml-1 hover:bg-muted-foreground/20"
                onClick={clearMerchantFilter}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
            <Link to="/admin/merchants/directory" className="text-sm text-muted-foreground hover:underline">
              ← Back to Directory
            </Link>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Merchants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{merchantsSummary.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{activeSources.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Paused / Deprecated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {pausedSources.length} / {deprecatedSources.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Merchants w/o Active
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-500">
                {merchantsWithNoActive.length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Source Type Summary Panel - CR082 */}
        <SourceTypeSummary sources={sources} />

        {/* Sources Table with Tabs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Source Registry
            </CardTitle>
            <CardDescription>
              All configured sources. Lower priority numbers are tried first during scans.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">
                  All <Badge variant="secondary" className="ml-1">{sources.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="active">
                  Active <Badge variant="secondary" className="ml-1">{activeSources.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="paused">
                  Paused <Badge variant="secondary" className="ml-1">{pausedSources.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="deprecated">
                  Deprecated <Badge variant="secondary" className="ml-1">{deprecatedSources.length}</Badge>
                </TabsTrigger>
                {merchantsWithNoActive.length > 0 && (
                  <TabsTrigger value="no-sources" className="text-orange-500">
                    ⚠ No Active Sources
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value={activeTab} className="mt-4">
                <SourcesTable
                  sources={getFilteredSources()}
                  merchants={merchants}
                  onEdit={setEditSource}
                  onToggleStatus={toggleStatus}
                  onDeprecate={deprecateSource}
                  onDelete={deleteSource}
                  onTestRead={handleTestRead}
                  onTestImageCapture={setTestCaptureSource}
                  onUpdatePriority={updatePriority}
                  onUpdateLogo={updateMerchantLogo}
                  onUploadLogo={uploadMerchantLogo}
                  onClearRecipeOverride={async (id) => {
                    await updateSource(id, { web_recipe_template_id: null } as any);
                    toast({ title: "Recipe override cleared" });
                  }}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <AddSourceModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onSave={addSource}
      />
      <EditSourceModal
        source={editSource}
        open={!!editSource}
        onOpenChange={(open) => !open && setEditSource(null)}
        onSave={updateSource}
      />
      {/* CR454: Test Image Capture Modal */}
      <TestImageCaptureModal
        source={testCaptureSource}
        open={!!testCaptureSource}
        onOpenChange={(open) => !open && setTestCaptureSource(null)}
      />
    </AdminLayout>
  );
}

